<script lang="ts">
  import AuthLayout from "./AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import { consumeAuthResult } from "$lib/api/endpoints";
  import { AppError } from "$lib/api/problem";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";

  let error = $state<AppError | null>(null);
  let started = false;

  function safePath(path: string | undefined): string {
    // Backslashes normalize to `/` in browsers, so `/\evil.com` ≡ `//evil.com`.
    if (!path || !path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return "/";
    return path;
  }

  $effect(() => {
    if (started) return;
    started = true;
    // The opaque one-time handle is the ONLY thing the browser receives — no
    // assertion, attributes, email, connection or org id (G-3).
    const handle = router.match.query.get("result");
    // Scrub `result` from history immediately so it can't be replayed from the URL.
    router.scrubQuery(["result"]);
    if (!handle) {
      router.navigate("/login", { replace: true });
      return;
    }
    void (async () => {
      try {
        const result = await consumeAuthResult(handle);
        if (result.status === "success") {
          // The ACS already set the HttpOnly session cookie; hydrate from it.
          await session.bootstrap();
          router.navigate(safePath(result.returnTo), { replace: true });
        } else {
          const code = result.code ?? "saml_authentication_failed";
          router.navigate(
            `/auth/error?code=${encodeURIComponent(code)}&ref=${encodeURIComponent(result.correlationId)}`,
            { replace: true }
          );
        }
      } catch (err) {
        if (err instanceof AppError) error = err;
      }
    })();
  });
</script>

<AuthLayout title="Completing sign-in">
  {#if error}
    <Banner tone="error" title="We couldn't complete sign-in" correlationId={error.correlationId}>
      {error.userMessage}
    </Banner>
    <Button href="/login" fullWidth>Back to sign in</Button>
  {:else}
    <p class="muted" role="status" aria-live="polite">Verifying your sign-in…</p>
  {/if}
</AuthLayout>
